import { ExclamationCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Divider, Space } from 'antd';
import GenTaxImage from 'assets/images/gen-tax-img-cropped.svg';
import { StyledCard } from 'components/Custom/card';
import {
  CUSTOMISE_COLUMNS_ICON_FONT_SIZE,
  CustomiseColumnsIconButtonWithTooltip,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customise-columns-icon-button';
import { ColumnCustomizationDrawer } from 'components/Custom/ColumnCustomizationDrawer';
import { CardDescSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { FilterSelectionDrawer } from 'components/MainComponent/FilterSelectionDrawer';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import { FILTERS_TRANSACTIONS, PAGE_SIZE_TRANSACTIONS } from 'pages/vat/report';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Provider, UpgradeMessageSource } from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import { DataLogStatus, DrawerType, WindowType } from 'store/app/enums';
import { getDrawerData, getDrawerOpen } from 'store/app/selectors';
import { DrawerData } from 'store/app/types';
import { getOrgOrganisations } from 'store/organisation/selectors';
import {
  exportToCsvRequest,
  fetchTransactionAttachmentRequest,
  getDataPageRequest,
  uploadTransactionAttachmentRequest,
} from 'store/vat/actions';
import { ExceptionsActionType } from 'store/vat/models';
import { DataJournal, JournalType } from 'store/vat/models/vatReportData';
import { useNumberFormat } from 'utils/numberFormat';
import { isResponsivePanelWideLayout } from 'utils/responsive-panel-breakpoints';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { PreferenceActions, PreferenceSections } from 'utils/preferences';
import { AttachmentViewer } from './components/AttachmentViewer/index';
import { PwaVatTransactionsCardList } from './components/PwaVatTransactionsCardList';
import { PwaVatTxnDetailSheet } from './components/PwaVatTxnDetailSheet';
import { getTransactionColumns } from './components/TransactionTableColumns';
import { TransactionTableFilters } from './components/TransactionTableFilters';
import { TransactionTableHeader } from './components/TransactionTableHeader';
import { TransactionTableWrapper } from './styles';
import { TransactionTableProps } from './types/transaction.types';
import {
  filterTransactions,
  formatDate,
  getTableWidth,
  journalTypeToGroupingType,
} from './utils/transaction.utils';

export const NOT_CLICKED_ATTACHMENT = 'NOT_CLICKED_ATTACHMENT';
const getTransactionAttachments = (
  transactions: DataJournal[] | undefined,
  transactionId: string
): any[] => {
  if (!transactions) return [];

  const transaction = transactions.find(
    (transaction) =>
      transaction.sourceId === transactionId ||
      transaction.invoiceId === transactionId
  );

  return transaction?.transactionAttachments
    ? transaction.transactionAttachments
    : [];
};

export const TransactionTable: React.FC<TransactionTableProps> = ({
  transData,
  //dataToExport,
  journalType,
  setJournalType,
  filteredBox,
  noSumData,
  summarizedStatusPending,
  handlePreferences,
  providerName,
  showSideMenu,
  isAdjustedReturn,
  menuSize,
  connectionId,
  setShowSideMenu,
  loading,
  attachmentLoading,
  total,
  reportId,
  connectedProperly,
  status,
  onLoadReport,
  vatRunData,
  vatReportData,
  currentSubscription,
  currentUser,
  currentOrganisation,
  hasAdvancedUserRights,
}) => {
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const dispatch = useDispatch();
  const { locale } = useNumberFormat();
  const organisations = useSelector(getOrgOrganisations);
  const drawerOpen = useSelector(getDrawerOpen);
  const drawerData = useSelector(getDrawerData);
  const transactionsColumnsDrawerOpen =
    drawerOpen && drawerData?.type === DrawerType.CUSTOMIZE_COLUMNS;

  const [customPagination] = React.useState(true);
  const [source, setSource] = React.useState(transData);
  const [showAdjustedTransactions, setShowAdjustedTransactions] =
    React.useState<boolean>(false);
  const [attachmentsTransactionId, setAttachmentsTransactionId] =
    React.useState<string>(NOT_CLICKED_ATTACHMENT);
  const [attachmentSourceUrl, setAttachmentSourceUrl] =
    React.useState<string>('');
  const [transactionReference, setTransactionReference] =
    React.useState<string>('');
  const [searchValue, setSearchValue] = React.useState<string>();
  const [loadTransactionsClicked, setLoadTransactionsClicked] =
    React.useState<boolean>(false);
  const [attachmentSourceType, setAttachmentSourceType] =
    React.useState<string>('');
  const [attachmentsUUID, setAttachmentsUUID] = React.useState<string>('');
  const [selectedPwaTransaction, setSelectedPwaTransaction] =
    React.useState<DataJournal | null>(null);
  const pwaDetailDragZoneRef = React.useRef<HTMLDivElement>(null);
  const closePwaTransactionDetail = React.useCallback(() => {
    setSelectedPwaTransaction(null);
  }, []);
  useOverlayBackHandler(
    Boolean(pwaMobileNav && selectedPwaTransaction),
    closePwaTransactionDetail,
    'vat-transactions-detail',
    pwaMobileNav
  );
  const pwaDetailDragClose = usePwaBottomSheetDragClose(
    pwaMobileNav && !!selectedPwaTransaction,
    closePwaTransactionDetail,
    pwaDetailDragZoneRef
  );

  const TRANSACTIONS_COLUMNS_CONFIG = 'transactions-columns-config';
  const TRANSACTIONS_VISIBLE_FILTERS = 'transactions-visible-filters';
  const TRANSACTIONS_ALWAYS_FIXED = [
    'netAmountConverted',
    'taxAmountConverted',
  ]; // Net and Tax must always be fixed at the end
  // On phone we still pin Net/Tax at the end of column order, but Ant `fixed`
  // sticky is stripped (see pwaSafeTransactionColumns) so the table can scroll.
  const transactionsAlwaysFixedColumns = TRANSACTIONS_ALWAYS_FIXED;
  const formatCardAmount = React.useCallback(
    (value?: number) =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0),
    [locale]
  );
  const formatCardText = React.useCallback(
    (value?: string | number | null) =>
      value === null || value === undefined || value === '' ? '-' : String(value),
    []
  );

  const getFiltersList = React.useCallback(() => {
    const provider = providerName?.toLowerCase();

    // Zoho has its own mapping; no account code filter there.
    if (provider === 'zoho') {
      return [
        'entity',
        'taxName',
        'selectedValue',
        'journalDate',
        'journalCreatedDate',
        'sourceType',
        'netAmountConverted',
      ];
    }

    // For Excel / Receipts providers we hide "accountCode" entirely (no filter).
    if (provider === 'excel' || provider === 'receipts') {
      return [
        'entity',
        'taxName',
        'selectedValue',
        'journalDate',
        'journalCreatedDate',
        'sourceType',
        'accountType',
        'accountName',
        'netAmountConverted',
      ];
    }

    // Default: include accountCode in filters.
    return [
      'entity',
      'taxName',
      'selectedValue',
      'journalDate',
      'journalCreatedDate',
      'sourceType',
      'accountCode',
      'accountType',
      'accountName',
      'netAmountConverted',
    ];
  }, [providerName]);

  // Default visible filters: Mapping, Tax Name, Created Date, A/c name, net amount (Entity off by default)
  const DEFAULT_VISIBLE_FILTERS_TRANSACTIONS = [
    'selectedValue',
    'taxName',
    'journalCreatedDate',
    'accountName',
    'netAmountConverted',
  ];
  // Phone: keep the filter strip lean; users add more via the + drawer.
  const DEFAULT_VISIBLE_FILTERS_TRANSACTIONS_PWA = [
    'selectedValue',
    'journalDate',
  ];

  const allFilters = React.useMemo(() => getFiltersList(), [getFiltersList]);
  const savedVisibleFilters = handlePreferences(
    PreferenceSections.TRANSACTIONS,
    PreferenceActions.GET,
    { name: TRANSACTIONS_VISIBLE_FILTERS }
  );
  const [visibleFilters, setVisibleFilters] = React.useState<string[]>(() => {
    // Phone: ignore a long desktop-saved filter strip so the list stays visible.
    if (pwaMobileNav) {
      return DEFAULT_VISIBLE_FILTERS_TRANSACTIONS_PWA.filter((f) =>
        allFilters.includes(f)
      );
    }
    if (
      savedVisibleFilters &&
      Array.isArray(savedVisibleFilters) &&
      savedVisibleFilters.length > 0
    ) {
      const valid = savedVisibleFilters.filter((f) => allFilters.includes(f));
      if (valid.length > 0) return valid;
    }
    return DEFAULT_VISIBLE_FILTERS_TRANSACTIONS.filter((f) =>
      allFilters.includes(f)
    );
  });

  const handleOpenFilterSelection = React.useCallback(() => {
    const drawerData = {
      title: t('submissions.select-filters-drawer-title'),
      subtitle: t('submissions.select-filters-subtitle'),
      type: DrawerType.FILTER_SELECTION,
      component: (
        <FilterSelectionDrawer
          availableFilters={allFilters}
          selectedFilters={visibleFilters}
          onUpdate={(selectedFilters) => {
            setVisibleFilters(selectedFilters);
            handlePreferences(
              PreferenceSections.TRANSACTIONS,
              PreferenceActions.SET,
              {
                name: TRANSACTIONS_VISIBLE_FILTERS,
                value: selectedFilters,
              }
            );
          }}
          onClose={() => dispatch(turnDrawerOff())}
          skipPreferenceSave
          mainLocaleKey="transactions"
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [allFilters, visibleFilters, handlePreferences, dispatch]);

  React.useEffect(() => {
    setVisibleFilters((prev) => {
      const valid = prev.filter((f) => allFilters.includes(f));
      if (valid.length === prev.length) return prev;
      const fallback = pwaMobileNav
        ? DEFAULT_VISIBLE_FILTERS_TRANSACTIONS_PWA
        : DEFAULT_VISIBLE_FILTERS_TRANSACTIONS;
      return valid.length > 0
        ? valid
        : fallback.filter((f) => allFilters.includes(f));
    });
  }, [allFilters, pwaMobileNav]);

  const pageSizeSaved = handlePreferences(
    PreferenceSections.TRANSACTIONS,
    PreferenceActions.GET,
    {
      name: PAGE_SIZE_TRANSACTIONS,
    }
  );

  // Get saved column configuration preferences (only once on mount)
  const savedConfig = React.useMemo(() => {
    const config = handlePreferences(
      PreferenceSections.TRANSACTIONS,
      PreferenceActions.GET,
      {
        name: TRANSACTIONS_COLUMNS_CONFIG,
      }
    );

    // If no saved preferences, return null (all columns visible by default)
    if (!config || typeof config !== 'object') {
      return null;
    }
    return config;
  }, []); // Only run once on mount

  // Define export-only columns (hidden by default)
  const exportOnlyColumns = [
    'sourceId',
    'netAmount',
    'taxAmount',
    'modifiedNetAmount',
    'modifiedTaxAmount',
    'grossAmount',
    'grossAmountConverted',
    'journalNumber',
    'paymentStatus',
    'appliedAmount',
    'taxType',
    'taxRate',
    'valueModifier',
    'error',
    'isProcessed',
    'deferredCount',
    'isReverseCharge',
    'reverseChargeAmount',
    'uploadDate',
    'sortId',
  ];

  // Default visible columns: standard columns (excluding export-only columns).
  // For Excel / Receipts providers we also hide "accountCode" entirely so it
  // doesn't appear in the table or customization.
  const defaultVisibleColumns = React.useMemo(() => {
    // Phone: fewer columns so portrait stays readable; users can add more via Customise.
    if (pwaMobileNav) {
      return [
        'sourceType',
        'journalDate',
        'accountName',
        'netAmountConverted',
        'taxAmountConverted',
      ];
    }
    const base = [
      'taxName',
      'journalCreatedDate',
      'journalDate',
      'sourceType',
      'accountCode',
      'accountType',
      'accountName',
      'contact',
      'invoiceNumber',
      'description',
      'reference',
      'currency',
    ];
    const provider = providerName?.toLowerCase();
    if (provider === 'excel' || provider === 'receipts') {
      return base.filter((col) => col !== 'accountCode');
    }
    return base;
  }, [providerName, pwaMobileNav]);

  const [visibleColumns, setVisibleColumns] = React.useState<string[] | null>(
    savedConfig?.visibleColumns || defaultVisibleColumns
  );
  const [lockedColumns, setLockedColumns] = React.useState<string[]>(
    savedConfig?.lockedColumns || []
  );
  const [columnOrder, setColumnOrder] = React.useState<string[] | undefined>(
    savedConfig?.columnOrder
  );

  // Save column configuration preferences
  const handleColumnsChange = React.useCallback(
    (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => {
      setVisibleColumns(config.visibleColumns);
      setLockedColumns(config.lockedColumns);
      setColumnOrder(config.columnOrder);
      handlePreferences(
        PreferenceSections.TRANSACTIONS,
        PreferenceActions.SET,
        {
          name: TRANSACTIONS_COLUMNS_CONFIG,
          value: config,
        }
      );
    },
    [handlePreferences]
  );

  const filterSavedRecord: Array<Record<string, string>> = [];
  const filtersSaved = handlePreferences(
    PreferenceSections.TRANSACTIONS,
    PreferenceActions.GET,
    {
      name: FILTERS_TRANSACTIONS,
    }
  );
  if (filtersSaved) {
    Object.keys(filtersSaved).forEach((key) =>
      filterSavedRecord.push({ key: key, value: filtersSaved[key] })
    );
  }

  // Baseline for persisted table filters (excluding header search). ScopeTable
  // stores the round search box under `search`; we never persist it so typing
  // does not trigger preference saves when nothing else changed.
  const lastSavedFiltersWithoutSearchRef = React.useRef<string | null>(null);
  if (lastSavedFiltersWithoutSearchRef.current === null) {
    if (filtersSaved && typeof filtersSaved === 'object') {
      const { search: _removed, ...rest } = filtersSaved as Record<
        string,
        unknown
      >;
      lastSavedFiltersWithoutSearchRef.current = JSON.stringify(rest);
    } else {
      lastSavedFiltersWithoutSearchRef.current = '';
    }
  }

  const resolveEntityForRow = React.useCallback(
    (journal: DataJournal) => {
      const isGroupContext =
        currentOrganisation?.providerName?.toLowerCase() ===
        Provider.GROUP.toLowerCase();
      if (isGroupContext) {
        const appConnectionId =
          (journal as any).applicationConnectionId ||
          (journal as any).applicationConnectionID;
        const orgByConnection = organisations?.find(
          (org) => org.uuid === appConnectionId
        );
        const groupResolvedName =
          orgByConnection?.friendlyName || orgByConnection?.legalName;
        if (groupResolvedName) {
          return groupResolvedName;
        }
        return (journal as any).entity ?? (journal as any).entityName;
      }
      return (
        currentOrganisation?.organisation ??
        (journal as any).entity ??
        (journal as any).entityName
      );
    },
    [
      currentOrganisation?.organisation,
      currentOrganisation?.providerName,
      organisations,
    ]
  );

  const resolveEntityProviderForRow = React.useCallback(
    (journal: DataJournal) => {
      const isGroupContext =
        currentOrganisation?.providerName?.toLowerCase() ===
        Provider.GROUP.toLowerCase();
      if (isGroupContext) {
        const appConnectionId =
          (journal as any).applicationConnectionId ||
          (journal as any).applicationConnectionID;
        const orgByConnection = organisations?.find(
          (org) => org.uuid === appConnectionId
        );
        return orgByConnection?.providerName ?? providerName;
      }
      return providerName;
    },
    [currentOrganisation?.providerName, organisations, providerName]
  );

  React.useEffect(() => {
    const enriched = transData?.map((j) => ({
      ...j,
      entityName: resolveEntityForRow(j),
      entity: resolveEntityForRow(j),
      entityProviderName: resolveEntityProviderForRow(j),
    }));
    setSource(enriched ?? transData);
  }, [transData, resolveEntityForRow, resolveEntityProviderForRow]);

  React.useEffect(() => {
    if (!customPagination) {
      let transactionsToShow: DataJournal[] | undefined = [];
      if (showAdjustedTransactions) {
        transactionsToShow = transData?.filter(
          (transaction) =>
            transaction.exceptionActionType !== undefined &&
            transaction.exceptionActionType === ExceptionsActionType.ADJUSTMENT
        );
      } else {
        transactionsToShow = transData;
      }
      const enriched = transactionsToShow?.map((j) => ({
        ...j,
        entityName: resolveEntityForRow(j),
        entity: resolveEntityForRow(j),
        entityProviderName: resolveEntityProviderForRow(j),
      }));
      setSource(enriched ?? transactionsToShow);
    }
  }, [
    showAdjustedTransactions,
    transData,
    resolveEntityForRow,
    resolveEntityProviderForRow,
  ]);

  const onAttachmentClick = (record: DataJournal) => {
    const trnIdentifier =
      providerName?.toLowerCase() === 'quickbooks'
        ? record.invoiceId
        : record.sourceId;

    setAttachmentsTransactionId(trnIdentifier);
    setAttachmentSourceUrl(record.sourceUrl);
    setAttachmentSourceType(record.sourceType);
    setAttachmentsUUID(record.uuid);

    const invoiceNumber = record.invoiceNumber?.trim();
    setTransactionReference(invoiceNumber ? `[${invoiceNumber}]` : '');
    //setShowSideMenu(false);
    dispatch(
      fetchTransactionAttachmentRequest(
        connectionId,
        trnIdentifier,
        record.uuid,
        providerName?.toLowerCase(),
        record.sourceType
      )
    );
  };

  const handleUploadAttachment = (file: File) => {
    // Use the same parameters as the get attachment call
    dispatch(
      uploadTransactionAttachmentRequest(
        connectionId,
        attachmentsTransactionId,
        attachmentsUUID,
        providerName?.toLowerCase() || '',
        file,
        attachmentSourceType
      )
    );
  };

  const handleSearch = (value: string) => {
    if (!customPagination) {
      setSource(filterTransactions(transData ? transData : [], value));
    } else {
      setSearchValue(value);
    }
  };

  const handleAdjustedTransactionsChange = (checked: boolean) => {
    setShowAdjustedTransactions(checked);
  };

  const savePageSize = (pageSize: number) => {
    handlePreferences(PreferenceSections.TRANSACTIONS, PreferenceActions.SET, {
      name: PAGE_SIZE_TRANSACTIONS,
      value: pageSize,
    });
  };

  const saveFilters = React.useCallback(
    (filters: any) => {
      const { search: _search, ...filtersWithoutSearch } = filters || {};
      const snapshot = JSON.stringify(filtersWithoutSearch);
      if (snapshot === lastSavedFiltersWithoutSearchRef.current) {
        return;
      }
      lastSavedFiltersWithoutSearchRef.current = snapshot;
      handlePreferences(PreferenceSections.TRANSACTIONS, PreferenceActions.SET, {
        name: FILTERS_TRANSACTIONS,
        value: filtersWithoutSearch,
      });
    },
    [handlePreferences]
  );

  const [tableFiltersState, setTableFiltersState] = React.useState<
    Record<string, any>
  >(() => {
    // Phone starts with no saved desktop filters. A stored Journal Date
    // range was hiding every downloaded row until Clear Filters.
    if (pwaMobileNav) return {};
    if (filtersSaved && typeof filtersSaved === 'object') {
      return { ...(filtersSaved as Record<string, any>) };
    }
    return {};
  });

  const activeJournalTypeRef = React.useRef(journalType ?? 'DETAILED');
  activeJournalTypeRef.current = journalType ?? 'DETAILED';

  const handleTableFiltersChange = React.useCallback(
    (next: Record<string, any>) => {
      setTableFiltersState(next);
      const { sort: _sort, search: _search, ...rest } = next;
      saveFilters(rest);
    },
    [saveFilters]
  );

  const handleServerFilter = React.useCallback(
    (filters: Record<string, any>, pageSize?: number) => {
      dispatch(
        getDataPageRequest(
          reportId,
          1,
          pageSize ?? (pageSizeSaved ? pageSizeSaved : 20),
          JournalType.TRANSACTION,
          journalTypeToGroupingType(activeJournalTypeRef.current),
          filters
        )
      );
    },
    [dispatch, reportId, pageSizeSaved]
  );

  const buildJournalPageFilters = React.useCallback(() => {
    const { search: _search, ...filtersWithoutSearch } = tableFiltersState;
    if (!filtersWithoutSearch.sort) {
      return {
        ...filtersWithoutSearch,
        sort: { value: ['journalDate', 'desc'] },
      };
    }
    return filtersWithoutSearch;
  }, [tableFiltersState]);

  const fetchJournalPage = React.useCallback(
    (groupingJournalType: string) => {
      dispatch(
        getDataPageRequest(
          reportId,
          1,
          pageSizeSaved ? pageSizeSaved : 20,
          JournalType.TRANSACTION,
          journalTypeToGroupingType(groupingJournalType),
          buildJournalPageFilters()
        )
      );
    },
    [
      buildJournalPageFilters,
      dispatch,
      pageSizeSaved,
      reportId,
    ]
  );

  const handleJournalTypeChange = (value: string) => {
    activeJournalTypeRef.current = value;
    setJournalType?.(value);
    setSelectedPwaTransaction(null);
    fetchJournalPage(value);
  };

  const prevSummarizedSizeRef = React.useRef(vatReportData?.summarizedSize ?? 0);
  React.useEffect(() => {
    const prev = prevSummarizedSizeRef.current;
    const next = vatReportData?.summarizedSize ?? 0;
    prevSummarizedSizeRef.current = next;
    if (next > 0 && prev === 0 && journalType === 'SUMMARISED') {
      fetchJournalPage('SUMMARISED');
    }
  }, [vatReportData?.summarizedSize, journalType, fetchJournalPage]);

  const onCloseAttachments = () => {
    setAttachmentsTransactionId(NOT_CLICKED_ATTACHMENT);
    setTransactionReference('');
    setShowSideMenu(
      isResponsivePanelWideLayout(window.innerWidth, window.innerHeight)
    );
  };

  const getRowClassName = (record: DataJournal) => {
    if (
      record.sourceId === attachmentsTransactionId ||
      record.invoiceId === attachmentsTransactionId
    ) {
      return 'highlighted-transaction-row';
    }
    if (record.exceptionActionType) {
      return 'exception-row';
    }
    return '';
  };

  const handleAttachmentViewUpgradeModal = () => {
    dispatch(
      turnModalOn({
        title: '',
        type: WindowType.WARNING,
        component: (
          <UpgradeMessage
            source={UpgradeMessageSource.ATTACHMENT_VIEW}
            onClose={() => dispatch(turnModalOff())}
            userAccountType={currentUser?.accountType}
          />
        ),
      })
    );
  };

  const transactionColumns = React.useMemo(
    () =>
      getTransactionColumns(
        providerName,
        onAttachmentClick,
        attachmentsTransactionId,
        connectedProperly,
        customPagination,
        currentSubscription,
        handleAttachmentViewUpgradeModal,
        currentOrganisation?.providerName?.toLowerCase() ===
          Provider.GROUP.toLowerCase()
          ? undefined
          : currentOrganisation?.organisation,
        locale,
        currentOrganisation?.organisationId,
        vatRunData?.shortCode
      ),
    [
      providerName,
      attachmentsTransactionId,
      connectedProperly,
      customPagination,
      currentSubscription,
      currentOrganisation?.providerName,
      currentOrganisation?.organisation,
      currentOrganisation?.organisationId,
      locale,
      vatRunData?.shortCode,
    ]
  );

  // Ant fixed left/right columns without scroll.x clip the middle on narrow viewports.
  // On PWA the grid is hidden (cards + pager only); still strip `fixed` if the table mounts.
  const pwaSafeTransactionColumns = React.useMemo(() => {
    if (!pwaMobileNav) {
      return transactionColumns;
    }
    return transactionColumns.map((col) => {
      if (!col || !('fixed' in col) || col.fixed == null) {
        return col;
      }
      const { fixed: _fixed, ...rest } = col as typeof col & {
        fixed?: 'left' | 'right' | boolean;
      };
      return rest;
    });
  }, [pwaMobileNav, transactionColumns]);

  const openTransactionsColumnsDrawer = React.useCallback(() => {
    const customizableColumns =
      transactionColumns
        ?.filter(
          (col) =>
            col.key &&
            !col.fixed &&
            !TRANSACTIONS_ALWAYS_FIXED.includes(String(col.key))
        )
        .map((col) => ({
          key: col.key as string,
          title: String(col.title ?? col.key ?? ''),
          fixed: col.fixed as boolean | 'left' | 'right' | undefined,
        })) || [];

    const defaultVisible =
      visibleColumns ||
      transactionColumns
        ?.filter((col) => col.key && !col.fixed)
        .map((col) => String(col.key)) ||
      [];

    const drawerPayload: DrawerData = {
      title: t('main.columns', 'Columns'),
      type: DrawerType.CUSTOMIZE_COLUMNS,
      component: (
        <ColumnCustomizationDrawer
          columns={customizableColumns}
          visibleColumns={defaultVisible}
          lockedColumns={lockedColumns}
          columnOrder={columnOrder}
          alwaysFixedColumns={transactionsAlwaysFixedColumns}
          onSave={handleColumnsChange}
          onClose={() => dispatch(turnDrawerOff())}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerPayload));
  }, [
    columnOrder,
    dispatch,
    handleColumnsChange,
    lockedColumns,
    t,
    transactionColumns,
    visibleColumns,
  ]);

  const handleAttachmentUploadUpgradeModal = () => {
    dispatch(
      turnModalOn({
        title: '',
        type: WindowType.WARNING,
        component: (
          <UpgradeMessage
            source={UpgradeMessageSource.ATTACHMENT_UPLOAD}
            onClose={() => dispatch(turnModalOff())}
            userAccountType={currentUser?.accountType}
          />
        ),
      })
    );
  };

  const exportToCsv = () => {
    dispatch(
      exportToCsvRequest(
        reportId,
        JournalType.TRANSACTION,
        journalTypeToGroupingType(journalType)
      )
    );
  };

  useEffect(() => {
    // Check if we should show the warning panel
    const shouldShowWarning =
      status &&
      (DataLogStatus[status] === DataLogStatus.SUBMITTED ||
        DataLogStatus[status] === DataLogStatus.CANCELLED) &&
      (!transData || transData.length === 0) &&
      (!vatReportData?.exceptions || vatReportData.exceptions.length === 0) &&
      !sessionStorage.getItem('just-submitted');

    if (!shouldShowWarning) {
      setLoadTransactionsClicked(true);
    }
  }, [transData, vatReportData]);

  const renderSubmittedWarning = () => {
    // Show warning if status is SUBMITTED or CANCELLED and there are no transactions
    const isSubmittedOrCancelled =
      status &&
      (DataLogStatus[status] === DataLogStatus.SUBMITTED ||
        DataLogStatus[status] === DataLogStatus.CANCELLED);
    const hasTransactions = transData && transData.length > 0;

    if (!isSubmittedOrCancelled || hasTransactions) {
      return null;
    }

    return (
      <StyledCard
        className={pwaMobileNav ? 'scope-pwa-transactions-download-card' : undefined}
        $nopadding
        fullsize
        width="100%"
        style={{
          boxShadow: '0px 4px 10px 0px #0000000D',
          marginBottom: '20px',
          display: 'flex',
          fontSize: 14,
          fontWeight: 400,
          padding: 0,
          maxWidth: 1150,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <div className="scope-pwa-transactions-download-card__layout">
          <div className="scope-pwa-transactions-download-card__content">
            <Space>
              <CardTitleLargeSpan style={{ marginBottom: 10 }}>
                {t('vat-report.download-transactions-title')}
              </CardTitleLargeSpan>
              <ExclamationCircleOutlined
                style={{ color: '#D13131', fontSize: 18 }}
              />
            </Space>
            <CardDescSpan style={{ marginBottom: 16 }}>
              {t('vat-report.download-transactions-description')}
            </CardDescSpan>
            <ScopeButton
              type="primary"
              width={pwaMobileNav ? '100%' : 'fit-content'}
              height="40px"
              style={{ fontSize: '13px', fontWeight: 700 }}
              onClick={onLoadReport}
              disabled={!onLoadReport}
            >
              {t('vat-report.download-transactions-button')}
            </ScopeButton>
          </div>

          <img
            className="scope-pwa-transactions-download-card__image"
            alt={t('vat-report.vat-checker-image-alt')}
            src={GenTaxImage}
          />
        </div>
      </StyledCard>
    );
  };

  // If warning should be shown, only render the warning panel
  if (!loadTransactionsClicked) {
    return (
      <>
        {attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT && (
          <AttachmentViewer
            onClose={onCloseAttachments}
            transactionReference={transactionReference}
            attachments={getTransactionAttachments(
              transData,
              attachmentsTransactionId
            )}
            attachmentSourceUrl={attachmentSourceUrl}
            attachmentLoading={attachmentLoading}
            providerName={providerName}
            onUploadAttachment={handleUploadAttachment}
            currentSubscription={currentSubscription}
            onUpgradeModalOpen={handleAttachmentUploadUpgradeModal}
            currentUser={currentUser}
            currentOrganisation={currentOrganisation}
            hasAdvancedUserRights={hasAdvancedUserRights}
          />
        )}
        {renderSubmittedWarning()}
      </>
    );
  }

  return (
    <>
      {attachmentsTransactionId !== NOT_CLICKED_ATTACHMENT && (
        <AttachmentViewer
          onClose={onCloseAttachments}
          transactionReference={transactionReference}
          attachments={getTransactionAttachments(
            transData,
            attachmentsTransactionId
          )}
          attachmentSourceUrl={attachmentSourceUrl}
          attachmentLoading={attachmentLoading}
          providerName={providerName}
          onUploadAttachment={handleUploadAttachment}
          currentSubscription={currentSubscription}
          onUpgradeModalOpen={handleAttachmentUploadUpgradeModal}
          currentUser={currentUser}
          currentOrganisation={currentOrganisation}
          hasAdvancedUserRights={hasAdvancedUserRights}
        />
      )}
      <StyledCard
        className={
          pwaMobileNav ? 'scope-pwa-vat-transactions-card-shell' : undefined
        }
        width={
          pwaMobileNav
            ? '100%'
            : getTableWidth(
                showSideMenu,
                menuSize,
                attachmentsTransactionId,
                loading
              )
        }
        style={
          pwaMobileNav
            ? { maxWidth: '100%' }
            : undefined
        }
      >
        {((!customPagination && transData && transData?.length > 0) ||
          customPagination) && (
          <TransactionTableHeader
            journalType={journalType}
            noSumData={noSumData}
            summarizedStatusPending={summarizedStatusPending}
            onSearch={handleSearch}
            onJournalTypeChange={handleJournalTypeChange}
            t={t}
            exportToCsv={exportToCsv}
            itemLabel={t('organisation.transactions') as 'Transactions'}
          />
        )}
        <Divider />
        <div
          className={
            pwaMobileNav ? 'scope-pwa-vat-transactions-body' : undefined
          }
          style={
            pwaMobileNav
              ? undefined
              : {
                  padding: '10px',
                  paddingLeft: '5px',
                  paddingTop: '10px',
                  margin: 'auto',
                }
          }
        >
          <TransactionTableWrapper>
            <div
              className={pwaMobileNav ? 'scope-pwa-entities-filters-row' : undefined}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: pwaMobileNav ? 6 : 12,
                marginBottom: pwaMobileNav ? 4 : showSideMenu ? 0 : 6,
              }}
            >
              <div
                className={pwaMobileNav ? 'scope-pwa-entities-filters' : undefined}
                style={{ flex: 1, minWidth: 0 }}
              >
                <ScopeTableFilters
                  filters={allFilters}
                  mainLocaleKey="transactions"
                  visibleFilters={visibleFilters}
                  onOpenFilterSelection={handleOpenFilterSelection}
                  value={tableFiltersState}
                  onChange={handleTableFiltersChange}
                  originalDataSource={source}
                  tableDataSource={source}
                  selectedPresetFilters={[
                    {
                      key: 'selectedValue',
                      value: filteredBox ? filteredBox : '',
                    },
                  ]}
                  appendedFilterComponent={
                    isAdjustedReturn ? (
                      <TransactionTableFilters
                        isAdjustedReturn={isAdjustedReturn}
                        showAdjustedTransactions={showAdjustedTransactions}
                        onAdjustedTransactionsChange={
                          handleAdjustedTransactionsChange
                        }
                        t={t}
                      />
                    ) : undefined
                  }
                  clearAppendedFilterComponent={() => {
                    setShowAdjustedTransactions(false);
                  }}
                  customPagination={customPagination}
                  onServerFilter={handleServerFilter}
                  embedded={false}
                  filterRowMarginBottom={0}
                  matchTriggerWidth={pwaMobileNav}
                  footerRightContent={
                    !pwaMobileNav && source && source.length > 0 ? (
                      <CustomiseColumnsIconButtonWithTooltip
                        type="default"
                        style={customiseColumnsIconButtonStyle(
                          transactionsColumnsDrawerOpen
                        )}
                        icon={
                          <SettingOutlined
                            style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                          />
                        }
                        onClick={() => openTransactionsColumnsDrawer()}
                      />
                    ) : undefined
                  }
                />
              </div>
              {!pwaMobileNav && source && source.length > 0 && (
                <div
                  className={
                    pwaMobileNav
                      ? 'scope-pwa-vat-transactions-columns-action'
                      : undefined
                  }
                  style={{
                    flexShrink: 0,
                    flexBasis: showSideMenu ? '100%' : undefined,
                    justifyContent: 'flex-end',
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop:showSideMenu ? 4 : 0,
                  }}
                >
                  <CustomiseColumnsIconButtonWithTooltip
                    type="default"
                    style={customiseColumnsIconButtonStyle(
                      transactionsColumnsDrawerOpen
                    )}
                    icon={
                      <SettingOutlined
                        style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                      />
                    }
                    onClick={() => openTransactionsColumnsDrawer()}
                  />
                </div>
              )}
            </div>
            {pwaMobileNav ? (
              <PwaVatTransactionsCardList
                rows={source || []}
                loading={loading}
                emptyMessage={t('global.no-data-available')}
                formatAmount={formatCardAmount}
                onOpenDetails={setSelectedPwaTransaction}
              />
            ) : null}
            <div
              className={
                pwaMobileNav
                  ? 'scope-pwa-vat-transactions-table-scroll scope-pwa-vat-transactions-table-scroll--pager-only'
                  : undefined
              }
            >
              <ScopeTable
                className="transaction-table"
                mainLocaleKey="transactions"
                defaultSort={['journalDate', 'desc']}
                externalFiltersState={tableFiltersState}
                tableDataSource={source}
                originalDataSource={source}
                columns={pwaSafeTransactionColumns}
                pagination={true}
                rowsPerPage={pageSizeSaved ? pageSizeSaved : 20}
                enableColumnsCustomization={!pwaMobileNav}
                hideToolbarColumnsCustomizationButton
                onColumnsChange={handleColumnsChange}
                visibleColumns={
                  pwaMobileNav
                    ? Array.from(
                        new Set([
                          'providerIcon',
                          'actions',
                          ...(visibleColumns || defaultVisibleColumns),
                          ...TRANSACTIONS_ALWAYS_FIXED,
                        ])
                      )
                    : visibleColumns || undefined
                }
                lockedColumns={pwaMobileNav ? [] : lockedColumns}
                columnOrder={
                  pwaMobileNav
                    ? [
                        'providerIcon',
                        'actions',
                        ...(columnOrder || []).filter(
                          (key) =>
                            key !== 'pwaDetail' &&
                            key !== 'providerIcon' &&
                            key !== 'actions'
                        ),
                      ]
                    : columnOrder
                }
                alwaysFixedColumns={transactionsAlwaysFixedColumns}
                rowHeight={pwaMobileNav ? 42 : 40}
                showSizeChanger={!pwaMobileNav}
                getNewPageSize={savePageSize}
                scroll={
                  pwaMobileNav
                    ? undefined
                    : {
                        x: 'max-content',
                      }
                }
                fontSize={12}
                headerFontSize={12}
                rowClassName={getRowClassName}
                total={total}
                reportId={reportId}
                transactionType={JournalType.TRANSACTION}
                customPagination={customPagination}
                journalType={journalType}
                searchFilterValue={searchValue}
                showAdjustedTransactions={showAdjustedTransactions}
              />
            </div>
            {pwaMobileNav && selectedPwaTransaction ? (
              <PwaVatTxnDetailSheet
                record={selectedPwaTransaction}
                onClose={closePwaTransactionDetail}
                formatAmount={formatCardAmount}
                formatText={formatCardText}
                formatDate={formatDate}
                dragZoneRef={pwaDetailDragZoneRef}
                dragCloseHandlers={pwaDetailDragClose}
              />
            ) : null}
          </TransactionTableWrapper>
        </div>
      </StyledCard>
    </>
  );
};
